import { ListOrdered, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';

import { Badge } from '@/ui/primitives/badge';
import { Button } from '@/ui/primitives/button';
import { DropdownMenu } from '@/ui/primitives/dropdown-menu';
import { Interruptor } from '@/ui/primitives/interruptor';
import { type ClassificacaoPersonalizada } from '@fiscoai/contracts';

import { FraseClassificacao } from './frase-classificacao';
import { uso } from './textos';

export interface AcoesClassificacao {
  aoAlternar: (ativar: boolean) => void;
  aoEditar: () => void;
  aoUsarEmRegra: () => void;
  aoApagar: () => void;
}

interface Props extends AcoesClassificacao {
  classificacao: ClassificacaoPersonalizada;
  podeEditar: boolean;
  ocupada: boolean;
}

function Menu({ classificacao: c, ...p }: Omit<Props, 'podeEditar' | 'ocupada' | 'aoAlternar'>) {
  return (
    <DropdownMenu
      gatilho={
        <Button variante="fantasma" tamanho="icone-sm" aria-label={`Mais ações: ${c.nome}`}>
          <MoreHorizontal aria-hidden />
        </Button>
      }
      itens={[
        { rotulo: 'Editar', icone: <Pencil aria-hidden />, aoSelecionar: p.aoEditar },
        ...(c.ativa
          ? [
              {
                rotulo: 'Usar como destino de uma regra',
                icone: <ListOrdered aria-hidden />,
                aoSelecionar: p.aoUsarEmRegra,
              },
            ]
          : []),
        {
          rotulo: 'Apagar',
          icone: <Trash2 aria-hidden />,
          aoSelecionar: p.aoApagar,
          perigoso: true,
        },
      ]}
    />
  );
}

/** Uma classificação: ativa/desativada, a frase do que ela faz e quantos itens e regras a usam. */
export function LinhaClassificacao({ classificacao: c, podeEditar, ocupada, ...acoes }: Props) {
  return (
    <li className="grid gap-1.5 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        {podeEditar ? (
          <Interruptor
            rotulo={c.nome}
            ligado={c.ativa}
            estado={c.ativa ? 'Ativa' : 'Desativada'}
            disabled={ocupada}
            aoMudar={acoes.aoAlternar}
          />
        ) : (
          <span className="flex items-center gap-2 font-medium">
            {c.nome}
            {!c.ativa && <Badge tom="neutro">Desativada</Badge>}
          </span>
        )}
        {podeEditar && <Menu classificacao={c} {...acoes} />}
      </div>
      <FraseClassificacao classificacao={c} />
      <p className="text-xs text-texto-fraco tabular">{uso(c)}</p>
    </li>
  );
}
